import { useMemo, useState } from 'react';
import { addMonths, format, parseISO, startOfMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CalendarDays } from 'lucide-react';

import { formatarData } from '@/nucleo/formato/datas';
import { Badge } from '@/ui/base/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { PageHeader } from '@/ui/PageHeader';
import { Spinner } from '@/ui/Spinner';

import { TIPO_META, useEventosDoCalendario, type EventoCalendario, type EventoTipo } from './dados';

/** Doze meses ao redor de hoje (seis para trás, cinco para frente), para o seletor. */
function opcoesDeMes(): Array<{ valor: string; rotulo: string }> {
  const hoje = new Date();
  return Array.from({ length: 12 }, (_, indice) => {
    const data = addMonths(hoje, indice - 6);
    return {
      valor: format(data, 'yyyy-MM'),
      rotulo: format(data, 'MMMM/yyyy', { locale: ptBR }),
    };
  });
}

/**
 * Calendário operacional: período letivo, vistorias, vencimentos de
 * documento, fim de vigência de termo e prazos legais, num único painel
 * filtrável por mês e por tipo.
 *
 * Tela somente leitura — não existe criação nem edição aqui, só a agregação
 * vinda da API (ver o comentário no topo de `dados.ts` sobre por que essa
 * agregação não pode acontecer no navegador).
 */
export function CalendarioOperacional() {
  const [mes, setMes] = useState<string>(() => format(new Date(), 'yyyy-MM'));
  const [filtro, setFiltro] = useState<EventoTipo | 'all'>('all');

  const opcoesMes = useMemo(opcoesDeMes, []);
  const inicioDoMes = useMemo(() => startOfMonth(parseISO(`${mes}-01`)), [mes]);

  const { data: eventos, isLoading, isError, refetch } = useEventosDoCalendario(mes);

  const filtrados = (eventos ?? []).filter((evento) => filtro === 'all' || evento.tipo === filtro);
  const grupos = filtrados.reduce<Record<string, EventoCalendario[]>>((acumulado, evento) => {
    (acumulado[evento.data] ??= []).push(evento);
    return acumulado;
  }, {});
  const contagem = (eventos ?? []).reduce<Record<string, number>>((acumulado, evento) => {
    acumulado[evento.tipo] = (acumulado[evento.tipo] ?? 0) + 1;
    return acumulado;
  }, {});

  return (
    <div className="container mx-auto py-6 space-y-6">
      <PageHeader
        title="Calendário operacional"
        description="Período letivo, vistorias agendadas, vencimentos de documentos, fins de termo e prazos legais — em um único calendário."
      />

      <div className="flex flex-wrap gap-3 items-center">
        <Select value={mes} onValueChange={setMes}>
          <SelectTrigger className="w-full sm:w-[180px]" aria-label="Mês do calendário">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {opcoesMes.map((opcao) => (
              <SelectItem key={opcao.valor} value={opcao.valor}>
                {opcao.rotulo}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filtro} onValueChange={(valor) => setFiltro(valor as EventoTipo | 'all')}>
          <SelectTrigger className="w-full sm:w-[220px]" aria-label="Filtrar por tipo de evento">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os tipos</SelectItem>
            {Object.entries(TIPO_META).map(([chave, meta]) => (
              <SelectItem key={chave} value={chave}>
                {meta.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex flex-wrap gap-1">
          {Object.entries(TIPO_META).map(([chave, meta]) => {
            const Icone = meta.icon;
            return (
              <Badge key={chave} variant="outline" className="gap-1 text-xs">
                <Icone className="h-3 w-3" /> {meta.label}: {contagem[chave] ?? 0}
              </Badge>
            );
          })}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarDays className="h-4 w-4" />
            {format(inicioDoMes, "MMMM 'de' yyyy", { locale: ptBR })}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Spinner />
          ) : isError ? (
            <ErrorState
              variant="load-failed"
              description="Não foi possível carregar os eventos do calendário agora. Verifique sua conexão e tente novamente."
              primaryAction={{ label: 'Tentar de novo', onClick: () => refetch() }}
            />
          ) : Object.keys(grupos).length === 0 ? (
            <EmptyState
              icon={CalendarDays}
              title="Sem eventos no período"
              description="Nenhum evento operacional registrado neste mês."
            />
          ) : (
            <div className="space-y-4">
              {Object.entries(grupos).map(([dia, eventosDoDia]) => (
                <div key={dia}>
                  <div className="text-xs font-medium text-muted-foreground mb-2">
                    {formatarData(dia)}
                  </div>
                  <div className="space-y-2">
                    {eventosDoDia.map((evento) => {
                      const meta = TIPO_META[evento.tipo];
                      const Icone = meta.icon;
                      return (
                        <div
                          key={evento.id}
                          className="flex items-start gap-3 rounded-md border p-3"
                        >
                          <div className={`rounded-md p-2 ${meta.tone}`}>
                            <Icone className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium">{evento.titulo}</div>
                            {evento.detalhe && (
                              <div className="text-xs text-muted-foreground">{evento.detalhe}</div>
                            )}
                          </div>
                          {evento.severidade && evento.severidade !== 'info' && (
                            <Badge
                              variant={
                                evento.severidade === 'destructive' ? 'destructive' : 'outline'
                              }
                            >
                              {evento.severidade === 'destructive' ? 'Vencido' : 'Atenção'}
                            </Badge>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
